import { Loader2 } from 'lucide-react';
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

interface Props {
  aiResult: any;
  isLoading?: boolean;
}

interface SectionCardProps {
  title: string;
  items: string[];
  genieIcon: GenieIconName;
  isLoading?: boolean;
}

const SectionCard = ({ title, items, genieIcon, isLoading }: SectionCardProps) => {
  if (!isLoading && (!items || items.length === 0)) return null;
  return (
    <div className="bg-white rounded-xl border-2 border-[#4E9CFF]/20 p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-4">
        <GenieIcon name={genieIcon} size={22} color="#4E9CFF" secondaryColor="#FF4DB8" gradientEnabled label={title} />
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
      </div>
      {isLoading ? (
        <div className="flex items-center gap-2 py-2">
          <Loader2 className="h-4 w-4 animate-spin text-[#4E9CFF]" />
          <span className="text-sm text-gray-500">Loading...</span>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((item, idx) => (
            <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 bg-gradient-to-br from-[#4E9CFF] to-[#FF4DB8]" />
              <span className="flex-1">{item}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const RecordingAttendeesCard = ({ aiResult, isLoading }: Props) => {
  const attendees: string[] = aiResult?.attendees || [];
  const speakerCount: number | null = aiResult?.speaker_count ?? null;

  if (!isLoading && attendees.length === 0) return null;

  return (
    <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-sm">
      <div className="rounded-[14px] bg-white overflow-hidden">
        <div
          className="px-5 py-4 border-b border-[#4E9CFF]/10"
          style={{ background: "linear-gradient(135deg, rgba(78,156,255,0.06) 0%, rgba(255,77,184,0.06) 100%)" }}
        >
          <div className="flex items-center gap-3">
            <GenieIcon name="users" size={24} color="#4E9CFF" secondaryColor="#FF4DB8" gradientEnabled label="Attendees" />
            <h3 className="text-base font-bold text-gray-900">Attendees</h3>
            {speakerCount !== null && (
              <span className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full bg-[#4E9CFF]/10 text-[#4E9CFF]">
                {speakerCount} {speakerCount === 1 ? "speaker" : "speakers"}
              </span>
            )}
          </div>
        </div>
        <div className="px-5 py-5">
          {isLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-[#4E9CFF]" />
              <span className="text-sm text-gray-500">Loading...</span>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {attendees.map((a, idx) => (
                <span
                  key={idx}
                  className="p-[1.5px] rounded-full bg-gradient-to-r from-[#4E9CFF]/40 to-[#FF4DB8]/40"
                >
                  <span className="block px-3 py-1 rounded-full bg-white text-xs font-semibold bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent">
                    {a}
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const RecordingDecisionsCard = ({ aiResult, isLoading }: Props) => (
  <SectionCard title="Decisions" genieIcon="clipboard-check" items={aiResult?.decisions || []} isLoading={isLoading} />
);

export const RecordingActionPlanCard = ({ aiResult, isLoading }: Props) => (
  <SectionCard title="Action Plan" genieIcon="target" items={aiResult?.action_plan || []} isLoading={isLoading} />
);

export const RecordingKeyTakeawaysCard = ({ aiResult, isLoading }: Props) => {
  const items: string[] = aiResult?.key_takeaways || [];
  if (!isLoading && items.length === 0) return null;

  return (
    <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-sm">
      <div className="rounded-[14px] bg-white overflow-hidden">
        <div
          className="px-5 py-4 border-b border-[#4E9CFF]/10"
          style={{ background: "linear-gradient(135deg, rgba(78,156,255,0.06) 0%, rgba(255,77,184,0.06) 100%)" }}
        >
          <div className="flex items-center gap-3">
            <GenieIcon name="brain" size={24} color="#4E9CFF" secondaryColor="#FF4DB8" gradientEnabled label="Key Takeaways" />
            <h3 className="text-base font-bold text-gray-900">Key Takeaways</h3>
          </div>
        </div>
        <div className="px-5 py-5">
          {isLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-[#4E9CFF]" />
              <span className="text-sm text-gray-500">Loading...</span>
            </div>
          ) : (
            <ul className="space-y-3">
              {items.map((item, idx) => (
                <li key={idx} className="p-[1.5px] rounded-xl bg-gradient-to-r from-[#4E9CFF]/30 to-[#FF4DB8]/30">
                  <div className="rounded-[10px] bg-white px-3.5 py-3">
                    <span className="text-sm text-gray-700 leading-relaxed">{item}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export const RecordingRecommendationsCard = ({ aiResult, isLoading }: Props) => (
  <SectionCard title="Recommendations" genieIcon="trending-up" items={aiResult?.recommendations || []} isLoading={isLoading} />
);

export const RecordingInsightsSection = ({ aiResult, isLoading }: Props) => (
  <>
    <RecordingAttendeesCard aiResult={aiResult} isLoading={isLoading} />
    <RecordingDecisionsCard aiResult={aiResult} isLoading={isLoading} />
    <RecordingActionPlanCard aiResult={aiResult} isLoading={isLoading} />
    <RecordingKeyTakeawaysCard aiResult={aiResult} isLoading={isLoading} />
    <RecordingRecommendationsCard aiResult={aiResult} isLoading={isLoading} />
  </>
);
